import { IComparer } from "../Comparers/IComparer";
import { ObjectValueComparer } from "../Comparers/ObjectValueComparer";
import { Constructor, Nullable } from "../Extensions";
import { NAMESPACE } from "./_Namespace";

export class ArrayHelper
{
    // #region Class Identity

    public static classNamespace = NAMESPACE + "ArrayHelper";
    public static className = "ArrayHelper";
    public static get classFullName() { return `${this.classNamespace}.${this.className}`; }

    // #endregion

    // #region Public Methods

    public static add<T>(array: T[], item: T): T[]
    {
        array.push(item);
        return array;
    }
    public static addRange<T>(array: T[], items: T[]): T[]
    {
        array.push(...items);
        return array;
    }
    public static all<T>(array: T[], predicate?: (item: T, index: number, array: T[]) => boolean): boolean
    {
        if (!predicate)
            return true

        return array.every(predicate);
    }
    public static any<T>(array: T[], predicate?: (item: T, index: number, array: T[]) => boolean): boolean
    {
        if (!predicate)
            return array.length > 0;

        return array.some(predicate);
    }
    public static clear<T>(array: T[]): T[]
    {
        array.splice(0, array.length);
        return array;
    }
    public static contains<T>(array: T[], item: T, comparer?: IComparer<T>): boolean
    {
        if (!comparer)
            return array.indexOf(item) > -1;

        return this.indexOfWithComparer(array, item, comparer) > -1;
    }
    public static count<T>(array: T[], predicate?: (item: T, index: number, array: T[]) => boolean): number
    {
        return (predicate ? array.filter(predicate) : array).length;
    }
    public static distinct<T>(array: T[], comparer?: IComparer<T>): T[]
    {
        return array.filter((item, index) =>
        {
            return this.indexOfWithComparer(array, item, comparer) === index;
        });
    }
    public static each<T>(array: T[], callbackfn: (item: T, index: number, array: T[]) => boolean | undefined | void): T[]
    {
        for (let i = 0; i < array.length; i++)
        {
            if (callbackfn(array[i], i, array) === false)
                break;
        }

        return array;
    }
    public static first<T>(array: T[], predicate?: (item: T, index: number, array: T[]) => boolean): T
    {
        const
            items = predicate ? array.filter(predicate) : array;

        if (items.length === 0)
            throw new Error("Sequence contains no elements");

        return items[0];
    }
    public static firstOrDefault<T>(array: T[], predicate?: (value: T, index: number, array: T[]) => unknown): Nullable<T>
    {
        const items = predicate ? array.filter(predicate) : array;
        return items.length > 0 ? items[0] : null;
    }
    public static groupBy<T, K, E, R>(array: T[], keySelector: (item: T) => K, elementSelector: (item: T) => E, resultSelector: (key: K, elements: E[]) => R): R[]
    {
        const
            keyOrder: K[] = [],
            map = new Map<K, E[]>();

        for (const item of array)
        {
            const key = keySelector(item);
            let existing: E[] | undefined;

            for (const [k, v] of map)
            {
                if (ObjectValueComparer.Default.compare(key, k) === 0)
                {
                    existing = v;
                    break;
                }
            }

            if (existing === undefined)
            {
                const elements = [elementSelector(item)];
                map.set(key, elements);
                keyOrder.push(key);
            }
            else
            {
                existing.push(elementSelector(item));
            }
        }

        return keyOrder.map(key =>
        {
            let elements: E[] = [];

            for (const [k, v] of map)
            {
                if (ObjectValueComparer.Default.compare(key, k) === 0)
                {
                    elements = v;
                    break;
                }
            }

            return resultSelector(key, elements);
        });
    }
    public static indexOfWithComparer<T>(array: T[], item: T, comparer?: IComparer<T>): number
    {
        if (!comparer)
            return array.indexOf(item);

        for (let i = 0; i < array.length; i++)
        {
            if (comparer.compare(item, array[i]) === 0)
                return i;
        }

        return -1;
    }
    public static isArrayLike(array: any): array is any[]
    {
        return array && typeof array.length === "number" && array.length >= 0 && typeof array.push === "function";
    }
    public static last<T>(array: T[], predicate?: (item: T, index: number, array: T[]) => boolean): T
    {
        const
            items = predicate ? array.filter(predicate) : array;

        if (items.length === 0)
            throw new Error("Sequence contains no elements");

        return items[items.length - 1];
    }
    public static lastOrDefault<T>(array: T[], predicate?: (item: T, index: number, array: T[]) => boolean): Nullable<T>
    {
        const
            items = predicate ? array.filter(predicate) : array;

        return items.length > 0 ? items[items.length - 1] : null;
    }
    public static max<T>(array: T[], filter?: (item: T, index: number, array: T[]) => boolean): Nullable<T>
    {
        const
            items = filter ? array.filter(filter) : array;

        let
            max: Nullable<T> = null;

        for (let i = 0; i < items.length; i++)
        {
            if (items[i] !== null)
            {
                if (max === null || max === undefined || max < items[i])
                    max = items[i];
            }
        }

        return max;
    }
    public static min<T>(array: T[], filter?: (item: T, index: number, array: T[]) => boolean): Nullable<T>
    {
        const
            items = filter ? array.filter(filter) : array;

        let
            min: Nullable<T> = null;

        for (let i = 0; i < items.length; i++)
        {
            if (items[i] !== null)
            {
                if (min === null || min === undefined || min > items[i])
                    min = items[i];
            }
        }

        return min;
    }
    public static ofType<T, U extends T>(array: T[], type: Constructor<U>): U[]
    {
        return array.filter(item => item instanceof type).map(item => item as U);
    }
    public static orderBy<T>(array: T[], selector: (item: T) => any): T[]
    {
        return array
            .map(item => ({ item, key: selector(item) }))
            .sort((a, b) => a.key < b.key ? -1 : a.key > b.key ? 1 : 0)
            .map(x => x.item);
    }
    public static orderByDescending<T>(array: T[], selector: (item: T) => any): T[]
    {
        return array
            .map(item => ({ item, key: selector(item) }))
            .sort((a, b) => a.key < b.key ? 1 : a.key > b.key ? -1 : 0)
            .map(x => x.item);
    }
    public static remove<T>(array: T[], item: T): T[]
    {
        for (let i = array.length - 1; i >= 0; i--)
        {
            if (array[i] === item)
                array.splice(i, 1);
        }

        return array;
    }
    public static removeWhere<T>(array: T[], selector: (item: T) => boolean): T[]
    {
        for (let i = array.length - 1; i >= 0; i--)
        {
            if (selector(array[i]))
                array.splice(i, 1);
        }

        return array;
    }
    public static select<T, U>(array: T[], callbackfn: (item: T, index: number, array: T[]) => U): U[]
    {
        return array.map(callbackfn);
    }
    public static selectMany<T, U>(array: T[], callbackfn: (item: T, index: number, array: T[]) => U[]): U[]
    {
        return array.flatMap(callbackfn);
    }
    public static sequenceEqualTo<T>(array: T[], arrayComparison: T[]): boolean
    {
        return (
            !!arrayComparison
            && arrayComparison.length === array.length
            && arrayComparison.every((item, index) =>
            {
                return item === array[index];
            })
        );
    }
    public static single<T>(array: T[], predicate?: (item: T, index: number, array: T[]) => boolean): T
    {
        const
            items = predicate ? array.filter(predicate) : array;

        if (items.length !== 1)
            throw new Error("Invalid Operation");

        return items[0];
    }
    public static singleOrDefault<T>(array: T[], predicate?: (item: T, index: number, array: T[]) => boolean): Nullable<T>
    {
        const
            items = predicate ? array.filter(predicate) : array;

        if (items.length > 1)
            throw new Error("Invalid Operation");

        return items.length === 1 ? items[0] : null;
    }
    public static skip<T>(array: T[], number: number): T[]
    {
        return array.slice(number);
    }
    public static sum<T>(array: T[], filter?: (item: T, index: number, array: T[]) => boolean): number
    {
        return (
            (filter ? array.filter(filter) : array)
                .reduce((a, b) => a + (typeof b === "number" ? b : 0), 0)
        );
    }
    public static take<T>(array: T[], number: number): T[]
    {
        return array.slice(0, number);
    }
    public static thenBy<T>(array: T[], selector: (item: T) => any): T[]
    {
        // Identical to orderBy — correct because Array.prototype.sort is stable (ES2019),
        // so elements equal on the secondary key preserve the relative order from the preceding orderBy pass.
        return this.orderBy(array, selector);
    }
    public static thenByDescending<T>(array: T[], selector: (item: T) => any): T[]
    {
        // Identical to orderByDescending — see thenBy comment.
        return this.orderByDescending(array, selector);
    }
    public static where<T>(array: T[], predicate: (value: T, index: number, array: T[]) => unknown, thisArg?: any): T[]
    {
        return array.filter(predicate, thisArg);
    }

    // #endregion
}